import { useEffect, useLayoutEffect, useMemo } from "react";
import { View } from "react-native";
import { CommonActions, StackActions } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { getBlock, getBlocksByTag } from "@src/packages/content";
import type {
  ContentBlock,
  ContentBlockId,
  ArticleBody,
} from "@src/packages/content";
import {
  useLocale,
  useTranslations,
  type TranslationFunction,
} from "@src/packages/i18n";
import type { ChatStackParamList } from "../../types";
import type { Article } from "../../components/ArticleSectionCard";
import {
  ArticleReaderContent,
  buildArticleChatPrompt,
} from "../../components/ArticleReaderSheet";
import { getTagsForSection } from "../../data/articleSectionTags";

type Props = NativeStackScreenProps<ChatStackParamList, "ArticleDetail">;

function blockToArticle(
  block: ContentBlock<"article">,
  t: TranslationFunction,
): Article {
  const body = block.body as ArticleBody;
  return {
    id: block.id,
    title: block.title,
    time: block.readTimeMinutes
      ? t("learn.read_time_minutes", {
          params: { minutes: String(block.readTimeMinutes) },
        })
      : "",
    tag: "",
    teaser: block.subtitle ?? "",
    body,
    tags: block.tags,
  };
}

export default function ArticleDetailScreen({ route, navigation }: Props) {
  const { articleId, returnTab, utm_source } = route.params;
  const { locale } = useLocale();
  const t = useTranslations();

  // locale dep re-derives when the active content locale flips, picking up
  // translated bodies from registerLocaleVariant().
  const article = useMemo(() => {
    void locale;
    const block = getBlock(articleId as ContentBlockId);
    if (!block || block.kind !== "article") return null;
    return blockToArticle(block as ContentBlock<"article">, t);
  }, [articleId, locale, t]);

  // If we know which section the user came from, surface other articles in
  // that section ("next in section" links). Deduped, current article filtered.
  const sectionArticles = useMemo<Article[] | undefined>(() => {
    void locale;
    if (!utm_source) return undefined;
    const tags = getTagsForSection(utm_source);
    if (!tags) return undefined;
    const seen = new Set<string>();
    const result: Article[] = [];
    for (const tag of tags) {
      for (const block of getBlocksByTag(tag)) {
        if (block.kind !== "article") continue;
        if (block.id === articleId) continue;
        if (seen.has(block.id)) continue;
        seen.add(block.id);
        result.push(blockToArticle(block as ContentBlock<"article">, t));
      }
    }
    return result.length > 0 ? result : undefined;
  }, [articleId, utm_source, locale, t]);

  useEffect(() => {
    if (!article) {
      navigation.goBack();
    }
  }, [article, navigation]);

  useLayoutEffect(() => {
    if (article) {
      navigation.setOptions({ title: article.title });
    }
  }, [article, navigation]);

  if (!article) return null;

  const handleClose = () => {
    if (returnTab) {
      // Reset the Learn stack so a later tap on the Learn tab shows the
      // hub instead of reopening this article, and the stale `returnTab`
      // on this route doesn't keep bouncing the user back to the origin.
      const state = navigation.getState();
      if (typeof state?.index === "number" && state.index > 0) {
        navigation.dispatch(StackActions.popToTop());
      }
      navigation
        .getParent()
        ?.dispatch(CommonActions.navigate({ name: returnTab }));
      return;
    }

    // On a deep-linked/shared article this is the only screen in the stack, so
    // goBack() would bubble to the tab navigator and pop to the initial Home
    // tab — fall back to the Learn list instead.
    const state = navigation.getState();
    if (typeof state?.index === "number" && state.index > 0) {
      navigation.goBack();
      return;
    }

    navigation.reset({ index: 0, routes: [{ name: "Learn" }] });
  };

  return (
    <View style={{ flex: 1 }}>
      <ArticleReaderContent
        article={article}
        onClose={handleClose}
        onOpenArticle={(next) =>
          navigation.replace("ArticleDetail", {
            articleId: next.id,
            // Preserve the entry section so siblings continue to surface
            // as the user clicks through related articles.
            ...(returnTab ? { returnTab } : {}),
            ...(utm_source ? { utm_source } : {}),
          })
        }
        onChatAboutArticle={() =>
          navigation.navigate("Assistant", {
            initialPrompt: buildArticleChatPrompt(article, t),
            autoSendInitialPrompt: true,
          })
        }
        sectionArticles={sectionArticles}
        skipExitAnimation
      />
    </View>
  );
}
